import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { addYears, startOfDay } from "date-fns";
import { RangeSelectorControlProps, useRangeSelectControl } from "@hooks";
import { STOCK_INDEXES } from "@constants";
import { ComparisonValue } from "../BaseChartTools";
import {
  SecuritiesChartSelectedType,
  SecuritiesChartType,
} from "../SecuritiesChart/types";

export interface ChartControlsValue {
  rangeProps: RangeSelectorControlProps;
  availableDateRanges?: IntervalType[];
  availableCompactDateRanges?: IntervalType[];
  chartType: SecuritiesChartType;
  defaultChartType: SecuritiesChartType;
  setChartType: (value: SecuritiesChartType) => void;
  comparedTicker: string | undefined;
  setComparedTicker: (value: string | undefined) => void;
  selectedAssetTypes: SecuritiesChartSelectedType[];
  setSelectedAssetTypes: (types: SecuritiesChartSelectedType[]) => void;
  availableAssetTypes: SecuritiesChartSelectedType[];
  setAvailableAssetTypes: (types: SecuritiesChartSelectedType[]) => void;
  comparisonValues: ComparisonValue[];
  controls: {
    showChartType: boolean;
    showCompare: boolean;
    showAssetTypes: boolean;
  };
}

const ChartControlsContext = createContext<ChartControlsValue | null>(null);

export const canShowAssetTypes = (value: ChartControlsValue): boolean =>
  value.controls.showAssetTypes && value.availableAssetTypes.length > 1;

export const canShowComparison = (value: ChartControlsValue): boolean =>
  value.controls.showCompare && value.comparisonValues.length > 0;

export const useChartControls = (): ChartControlsValue => {
  const context = useContext(ChartControlsContext);
  if (!context) {
    throw new Error(
      "useChartControls must be used within ChartControlsProvider"
    );
  }
  return context;
};

interface ProviderProps {
  children: ReactNode;
  rangeId: string;
  rangeFirstDate?: Date;
  availableDateRanges?: IntervalType[];
  availableCompactDateRanges?: IntervalType[];
  comparisonValues?: ComparisonValue[];
  controls?: {
    showChartType?: boolean;
    showCompare?: boolean;
    showAssetTypes?: boolean;
  };
  defaultChartType?: SecuritiesChartType;
}

export const ChartControlsProvider = ({
  children,
  rangeId,
  rangeFirstDate,
  availableDateRanges,
  availableCompactDateRanges,
  comparisonValues = STOCK_INDEXES,
  controls,
  defaultChartType = "valuation",
}: ProviderProps) => {
  const firstDate = rangeFirstDate ?? startOfDay(addYears(new Date(), -1));
  const rangeProps = useRangeSelectControl({ id: rangeId, firstDate });
  const [chartType, setChartTypeState] =
    useState<SecuritiesChartType>(defaultChartType);
  const [comparedTicker, setComparedTicker] = useState<string | undefined>();

  // Only performance can be compared, so the valuation chart drops the
  // comparison; picking one switches back to performance.
  const setChartType = useCallback((value: SecuritiesChartType) => {
    setChartTypeState(value);
    if (value === "valuation") {
      setComparedTicker(undefined);
    }
  }, []);
  const [selectedAssetTypes, setSelectedAssetTypes] = useState<
    SecuritiesChartSelectedType[]
  >(["all"]);
  const [availableAssetTypes, setAvailableAssetTypes] = useState<
    SecuritiesChartSelectedType[]
  >([]);

  // Setting availability also prunes the selection to what's still available
  // (keeping "all"), so a type selected on one depot doesn't leave a blank
  // series after switching to a depot that lacks it.
  const applyAvailableAssetTypes = useCallback(
    (types: SecuritiesChartSelectedType[]) => {
      setAvailableAssetTypes(types);
      setSelectedAssetTypes((prev) => {
        const next = prev.filter((t) => t === "all" || types.includes(t));
        return next.length ? next : ["all"];
      });
    },
    []
  );

  const showChartType = controls?.showChartType ?? false;
  const showCompare = controls?.showCompare ?? false;
  const showAssetTypes = controls?.showAssetTypes ?? false;

  const value = useMemo<ChartControlsValue>(
    () => ({
      rangeProps,
      availableDateRanges,
      availableCompactDateRanges,
      chartType,
      defaultChartType,
      setChartType,
      comparedTicker,
      setComparedTicker,
      selectedAssetTypes,
      setSelectedAssetTypes,
      availableAssetTypes,
      setAvailableAssetTypes: applyAvailableAssetTypes,
      comparisonValues,
      controls: { showChartType, showCompare, showAssetTypes },
    }),
    [
      rangeProps,
      availableDateRanges,
      availableCompactDateRanges,
      chartType,
      defaultChartType,
      setChartType,
      comparedTicker,
      selectedAssetTypes,
      availableAssetTypes,
      applyAvailableAssetTypes,
      comparisonValues,
      showChartType,
      showCompare,
      showAssetTypes,
    ]
  );

  return (
    <ChartControlsContext.Provider value={value}>
      {children}
    </ChartControlsContext.Provider>
  );
};
